import React from 'react';
import Config from '../../../../config';
import Typography from '@material-ui/core/Typography';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Paper from '@material-ui/core/Paper';
import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';

import CleverIcon from '../../../../assets/clever-logo.png';
import ClassLinkIcon from '../../../../assets/classlink-logo.png';
import { Box } from '@material-ui/core';

const useStyles = makeStyles((theme: Theme) => {
  return {
    panelSummaryContent: {
      margin: `${theme.spacing(2.5)}px 0`
    },
    detailsContainer: {
      paddingTop: 0
    },
    stepsContainer: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
      paddingLeft: theme.spacing(7.5),
      paddingRight: theme.spacing(7.5)
    },
    avatar: {
      ...theme.typography.h3,
      height: 48,
      width: 48,
      color: theme.palette.secondary.main,
      backgroundColor: '#F7FBFF',
      alignItems: 'center',
      margin: 'auto',
      marginRight: theme.spacing(2)
    },
    imagePanel: {
      display: 'flex'
    },
    link: {
      textDecoration: 'none'
    },
    buttonContainer: {
      display: 'flex',
      justifyContent: 'center',
      paddingTop: theme.spacing(2)
    },
    downloadButton: {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1)
    },
    imageContainer: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 4,
      height: 96,
      width: 96,
      display: 'flex',
      marginRight: theme.spacing(3)
    },
    documentContainer: {
      backgroundColor: '#F7FBFF',
      padding: theme.spacing(4),
      display: 'flex',
      flexDirection: 'column'
    },
    documentHeader: {
      margin: 'auto'
    },
    cleverLogo: {
      width: 76,
      margin: 'auto'
    },
    classLinkLogo: {
      width: 46,
      margin: 'auto'
    }
  };
});

const SingleSignOnPanel: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation('SingleSignOnPanel');
  const cleverInstructionDownload = `${Config.s3Url}/docs/Clever_Sharing_Guide_v4.pdf`;
  const classLinkInstructionDownload = `${Config.s3Url}/docs/ClassLink_Sharing_Guide_v4.pdf`;

  return (
    <Grid item key={`single-sign-on`}>
      <ExpansionPanel>
        <ExpansionPanelSummary
          aria-controls="single-sign-on-content"
          id="single-sign-on-content"
          classes={{ content: classes.panelSummaryContent }}
          expandIcon={<ExpandMoreIcon />}
        >
          <Avatar className={classes.avatar}>1</Avatar>
          <Grid container spacing={0} alignItems="center" justify="space-between">
            <Grid item>
              <Typography variant="h3">{t('Single Sign On/Roster Management')}</Typography>
              <Typography variant="subtitle1">
                {t('All documents and set up instructions for Clever or ClassLink users.')}
              </Typography>
            </Grid>
            <Grid item className={classes.imagePanel}>
              <Paper className={classes.imageContainer} elevation={0}>
                <img src={CleverIcon} alt={t('Clever')} className={classes.cleverLogo} />
              </Paper>
              <Paper className={classes.imageContainer} elevation={0}>
                <img src={ClassLinkIcon} alt={t('ClassLink')} className={classes.classLinkLogo} />
              </Paper>
            </Grid>
          </Grid>
        </ExpansionPanelSummary>
        <ExpansionPanelDetails className={classes.detailsContainer}>
          <Grid container spacing={0} direction="column">
            <Grid item>
              <Divider />
            </Grid>
            <Grid item>
              <Grid container spacing={3} className={classes.stepsContainer}>
                <Grid item xs={12}>
                  <Paper className={classes.documentContainer} elevation={0}>
                    <Typography className={classes.documentHeader} variant="h3">
                      {t('Single Sign On/Roster Management Resources')}
                    </Typography>
                    <Box className={classes.buttonContainer}>
                      <a
                        href={cleverInstructionDownload}
                        className={classes.link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button
                          variant="contained"
                          color="primary"
                          disableElevation
                          className={classes.downloadButton}
                        >
                          {t('Clever Instructions')}
                        </Button>
                      </a>
                      <a
                        href={classLinkInstructionDownload}
                        className={classes.link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button
                          variant="contained"
                          color="primary"
                          disableElevation
                          className={classes.downloadButton}
                        >
                          {t('ClassLink Instructions')}
                        </Button>
                      </a>
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h3">{t('Step 1')}</Typography>
                  <Typography variant="subtitle1">
                    {t('NEW and RETURNING users: ')}
                    <a href={Config.rosteringSupportUrl} target="_blank" rel="noopener noreferrer">
                      {t('Click here')}
                    </a>
                    {t(' to send us a message to schedule your requested launch date.')}
                  </Typography>
                  <Typography variant="subtitle1">
                    {t(
                      'Note: Please view our Clever or ClassLink Sharing Guide (above) for best practices.'
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h3">{t('Step 2')}</Typography>
                  <Typography variant="subtitle1">
                    {t(
                      'An Onboarding Team Member will contact you to discuss any outstanding actions to take in order to perform the initial data sync.'
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h3">{t('Step 3')}</Typography>
                  <Typography variant="subtitle1">
                    {t(
                      'When data sharing is approved, we will perform the initial data sync and notify you with next steps.'
                    )}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </ExpansionPanelDetails>
      </ExpansionPanel>
    </Grid>
  );
};

export default SingleSignOnPanel;
